import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Mail, KeyRound, Copy } from "lucide-react";
import { useAuth, formatApiError } from "@/lib/auth";

export default function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const data = await forgotPassword(email);
      setResult(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <main className="max-w-md mx-auto px-5 pt-24 pb-16">
      <div className="text-center mb-8">
        <KeyRound className="w-10 h-10 text-emerald-500 mx-auto" />
        <h1 className="fl-display text-3xl font-extrabold mt-3">RESET YOUR PASSWORD</h1>
        <p className="text-slate-400 text-sm mt-1">Enter your account email and we'll generate a reset link.</p>
      </div>

      {!result ? (
        <form onSubmit={submit} className="fl-card p-6 space-y-4">
          <label className="block">
            <span className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Email</span>
            <div className="relative mt-1.5">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input type="email" required className="fl-input pl-9" value={email} onChange={e => setEmail(e.target.value)} data-testid="forgot-email-input" />
            </div>
          </label>
          {error && <div className="text-sm text-red-400">{error}</div>}
          <button type="submit" disabled={busy} className="fl-btn w-full" data-testid="forgot-submit">{busy ? "Generating…" : "Send reset link"}</button>
          <div className="text-xs text-center text-slate-400">
            Remembered it? <Link to="/login" className="text-emerald-500 hover:underline">Sign in</Link>
          </div>
        </form>
      ) : (
        <div className="fl-card p-6 space-y-4" data-testid="forgot-success">
          <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-emerald-500">// {result.first_time ? "First-time password setup" : "Reset link generated"}</div>
          <p className="text-sm text-slate-300">{result.message}</p>
          {result.reset_link && (
            <div>
              <div className="fl-mono text-[10px] tracking-widest uppercase text-slate-500 mb-2">{result.first_time ? "Your setup link" : "Your reset link"} (would normally be emailed)</div>
              <div className="flex gap-2">
                <input className="fl-input flex-1 text-xs" readOnly value={result.reset_link} data-testid="reset-link-output" />
                <button onClick={() => navigator.clipboard?.writeText(result.reset_link)} className="fl-btn ghost !py-2 !px-3 text-xs"><Copy className="w-4 h-4" /></button>
              </div>
              <a href={result.reset_link} className="fl-btn w-full mt-3" data-testid="open-reset-link">{result.first_time ? "Set my password" : "Open reset page"}</a>
            </div>
          )}
          <p className="text-xs text-slate-500">{result.first_time ? "Heads up — you originally signed in with Google. Setting a password here lets you also sign in with email & password going forward. Google sign-in keeps working too." : "Email delivery (SendGrid) ships next — for now we generate the link directly so you can finish the flow."}</p>
        </div>
      )}
    </main>
  );
}
